/**
 * 金额与比例 —— M-14 的落地处。
 *
 * 规矩只有一条,但它是整套对账等式的地基:
 *   金额一律整数,单位分;比例一律整数 bp(800 = 8%);中间过程不许出现浮点。
 *
 * 为什么这么较真:浮点金额的错误不抛异常、不进日志、不触发告警,
 * 它只在月底对账时表现为「差了一分钱」,然后花三天去找那一分钱在哪。
 *
 * 所以下面的除法用 BigInt 做 —— JS 的 number 是双精度浮点,
 * `Math.floor(a * bp / 10000)` 在 a 很大时会先丢精度再取整,而丢精度这件事是静默的。
 */

/** 金额,单位分。恒为非负整数 */
export type Cents = number

/** 比例,单位 bp。0 ~ 10000,10000 = 100% */
export type RateBp = number

export const BP_DENOMINATOR = 10000

export class MoneyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'MoneyError'
  }
}

/** 断言是非负整数。不是就抛 —— 金额上的坏值必须当场炸,不许往下游漂 */
export function assertCents(value: unknown, what = '金额'): asserts value is Cents {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new MoneyError(`${what}必须是非负整数分,实际拿到 ${JSON.stringify(value)}`)
  }
}

/** 断言是合法的 bp。0 ~ 10000 的整数 */
export function assertRateBp(value: unknown, what = '比例'): asserts value is RateBp {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0 || value > BP_DENOMINATOR) {
    throw new MoneyError(`${what}必须是 0~${BP_DENOMINATOR} 的整数 bp(800 = 8%),实际拿到 ${JSON.stringify(value)}`)
  }
}

/**
 * 按 bp 算佣金,向下取整到分,尾差归平台(M-10)。
 *
 * 用 BigInt 而不是 `Math.floor(base * rateBp / 10000)`:
 * 后者的乘积可能超出安全整数范围,超出之后结果仍然是个「看起来正常的数」。
 */
export function commissionFromBp(baseCents: Cents, rateBp: RateBp): Cents {
  assertCents(baseCents, '佣金基数')
  assertRateBp(rateBp, '佣金率')
  const product = BigInt(baseCents) * BigInt(rateBp)
  // 两个操作数都非负,BigInt 的截断除法在这里等价于向下取整
  return Number(product / BigInt(BP_DENOMINATOR))
}

/** 把 bp 渲染成给人看的百分比字符串。只用于展示,不许拿回去参与计算 */
export function formatRateBp(rateBp: RateBp): string {
  assertRateBp(rateBp)
  const whole = Math.trunc(rateBp / 100)
  const frac = rateBp % 100
  return frac === 0 ? `${whole}%` : `${whole}.${String(frac).padStart(2, '0')}%`
}

/**
 * 把整数分渲染成给人看的「元」字符串(不带单位):`7000` → `70.00`、
 * `128000` → `1,280.00`(千分位,2026-10-11 对齐画板)。负数带前导 `-`。
 *
 * **只用于展示,不许拿回去参与计算**(M-14)。
 *
 * 为什么放进共享包:小程序里原本有两份手写的 `yuan()`(客户下单页与飞手订单池),
 * 实现还略有不同(一份 `Math.floor`,一份 `Math.trunc` 带负号处理)—— 两份展示
 * 实现迟早会在某一档数上分叉,而「同一个金额在两张页面上差一分钱」没有任何人会报。
 *
 * 为什么商与余数分别算:`Math.trunc(cents / 100)` 要先过一遍浮点除法,
 * 在极大值上会先丢精度再取整,而丢精度是静默的 —— 与 commissionFromBp 同一个理由。
 *
 * 千分位只对**已经是字符串的整数部分**插逗号(从右往左每三位一个),
 * 不碰任何数值运算 —— 分还是分,逗号是渲染,与「金额全程整数分」不冲突。
 * 不走 `toLocaleString`:它的分隔样式跟着运行环境变,而金额的显示格式必须是确定的。
 */
export function formatCentsYuan(cents: Cents): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  const whole = String((abs - (abs % 100)) / 100)
  // \B(不在串首)+ 先行断言(右侧剩余位数是 3 的倍数)= 三位一组的千分位
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${sign}${grouped}.${String(abs % 100).padStart(2, '0')}`
}

/** 把人填的百分比(如 "8" / "8.25")转成整数 bp。转不了就抛,不许猜 */
export function parsePercentToBp(input: string): RateBp {
  const m = /^\s*(\d{1,3})(?:\.(\d{1,2}))?\s*%?\s*$/.exec(input)
  if (!m) throw new MoneyError(`看不懂的百分比写法:${JSON.stringify(input)}`)
  const whole = Number(m[1] ?? '0')
  const frac = Number((m[2] ?? '').padEnd(2, '0'))
  const bp = whole * 100 + frac
  assertRateBp(bp, '佣金率')
  return bp
}
